import { useEffect, useMemo, useState } from 'react'
import { useHistory } from '../../../router/history'
import type { ClienteDTO, PagaebleResult, ReporteClienteDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke, { LONG_TIMEOUT_MS } from '../../../utils/APIInvoke'
import {
    ALL_ROWS,
    downloadCsv,
    errorMessage,
    formatSlashDate,
    formatSlashDateTime,
    labelize,
    matchesSearch,
    PAGE_SIZES,
    pageWindow,
    paginate,
    personName,
} from '../common/report-common'
import type { ReportColumn } from '../common/report-common'

const LOAD_ERROR = 'No se pudo cargar el reporte de clientes.'

export interface CustomerReportRow {
    id: number
    nombre: string
    creacion: string
    nacimiento: string
    rfc: string
    sexo: string
    telefono: string
    correo: string
    polizas: number
    robot: boolean
}

const toRow = (item: ReporteClienteDTO): CustomerReportRow => ({
    id: item.id,
    nombre: personName(item),
    creacion: formatSlashDateTime(item.fecha) || '',
    nacimiento: formatSlashDate(item.fecNacimiento) || '',
    rfc: String(item.rfc || '').toUpperCase(),
    sexo: labelize(item.sexo) || '',
    telefono: item.telefono || '',
    correo: item.correo || '',
    polizas: Number(item.polizas) || 0,
    robot: Boolean(item.esRobot),
})

export const CSV_COLUMNS: ReportColumn<CustomerReportRow>[] = [
    { key: 'nombre', label: 'Cliente', csv: (row) => row.nombre },
    { key: 'rfc', label: 'RFC', csv: (row) => row.rfc },
    { key: 'sexo', label: 'Género', csv: (row) => row.sexo },
    { key: 'nacimiento', label: 'Fecha de nacimiento', csv: (row) => row.nacimiento },
    { key: 'telefono', label: 'Teléfono', csv: (row) => row.telefono },
    { key: 'correo', label: 'Correo', csv: (row) => row.correo },
    { key: 'polizas', label: 'Pólizas', csv: (row) => row.polizas },
    { key: 'creacion', label: 'Fecha de creación', csv: (row) => row.creacion },
    { key: 'robot', label: 'Robot', csv: (row) => (row.robot ? 'Sí' : 'No') },
]

export const useReportCustomersModel = () => {
    const history = useHistory()
    const [cliente, setCliente] = useState<ClienteDTO | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [items, setItems] = useState<ReporteClienteDTO[]>([])
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [detailId, setDetailId] = useState<number | null>(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const filter = cliente?.id ? `&id=${cliente.id}` : ''
                const payload: WrapperResponse<PagaebleResult<ReporteClienteDTO>> | null = await (await APIInvoke.invokeGET(`/reportes/clientes?page=1&size=${ALL_ROWS}${filter}`, { timeoutMs: LONG_TIMEOUT_MS })).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setPage(1)
            } catch (loadError) {
                if (active) {
                    setItems([])
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [cliente, reloadToken])

    const allRows = useMemo(() => items.map(toRow), [items])
    const filteredRows = useMemo(
        () => allRows.filter((row) => matchesSearch(search, row.nombre, row.rfc, row.correo, row.telefono)),
        [allRows, search],
    )
    const paged = paginate(filteredRows, page, pageSize)
    const withPolicies = allRows.filter((row) => row.polizas > 0).length

    return {
        cliente,
        loading,
        error,
        count: allRows.length,
        withPolicies,
        withoutPolicies: allRows.length - withPolicies,
        robots: allRows.filter((row) => row.robot).length,
        search,
        rows: paged.rows,
        filteredCount: filteredRows.length,
        page: paged.page,
        totalPages: paged.totalPages,
        pages: filteredRows.length > 0 ? pageWindow(paged.page, paged.totalPages) : [],
        pageSize,
        rangeFrom: paged.rangeFrom,
        rangeTo: paged.rangeTo,
        detailId,
        changeCliente: setCliente,
        changeSearch: (value: string) => {
            setSearch(value)
            setPage(1)
        },
        setPage,
        changePageSize: (value: number) => {
            setPageSize(value)
            setPage(1)
        },
        exportCsv: () => downloadCsv('reporte-clientes', CSV_COLUMNS, filteredRows),
        reload: () => setReloadToken((token) => token + 1),
        openCliente: (id: number) => setDetailId(id),
        closeCliente: () => setDetailId(null),
        editCliente: (id: number) => history.push({ pathname: `/clientes/${id}/edit`, state: { backUrl: '/reportes/clientes' } }),
    }
}
